import { useEffect, useMemo, useRef, useState } from "react";
import { sendAiMessage } from "../../api/ai";
import { useAICopilotStore } from "../../ai/copilot";
import { ApiClientError } from "../../api/types";

function MarkdownLine({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function AiMarkdown({ content }: { content: string }) {
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const bullets = lines.filter((line) => /^[-•*]\s+/.test(line.trim()));
  const paras = lines.filter((line) => !/^[-•*]\s+/.test(line.trim()));
  return (
    <div className="space-y-2 text-xs leading-5">
      {paras.map((p, i) => (
        <p key={i}>
          <MarkdownLine text={p} />
        </p>
      ))}
      {bullets.length > 0 && (
        <ul className="list-disc list-inside space-y-1">
          {bullets.map((b, i) => (
            <li key={i}>
              <MarkdownLine text={b.replace(/^[-•*]\s+/, "")} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function buildContextEnvelope(state: ReturnType<typeof useAICopilotStore.getState>, message: string): string {
  const route = state.currentRoute || "unknown";
  const entityType = state.activeEntity.type || "none";
  const entityId = state.activeEntity.id || "none";
  const entityLabel = state.activeEntity.label || "none";
  const anomalyLines = state.activeAnomalies.length
    ? state.activeAnomalies
        .slice(0, 5)
        .map((item) => `- [${item.severity}] ${item.message}${item.contextKey ? ` (${item.contextKey})` : ""}`)
        .join("\n")
    : "- none";
  const summaryStats = JSON.stringify(state.screenSnapshot?.summaryStats ?? {});
  const visibleData = JSON.stringify((state.screenSnapshot?.visibleData ?? []).slice(0, 10));
  const focusedCell = JSON.stringify(state.screenSnapshot?.focusedCell ?? null);

  return [
    "SYSTEM CONTEXT FOR ERP COPILOT:",
    `Route: ${route}`,
    `Active Entity: ${entityType} | ${entityId} | ${entityLabel}`,
    `Snapshot Type: ${state.screenSnapshot?.viewType ?? "none"}`,
    `Summary Stats: ${summaryStats}`,
    `Focused Cell: ${focusedCell}`,
    "Active Anomalies:",
    anomalyLines,
    `Visible Grid Rows: ${visibleData}`,
    "",
    "USER QUESTION:",
    message,
  ].join("\n");
}

export function AICopilotDrawer() {
  const isOpen = useAICopilotStore((state) => state.isOpen);
  const isProcessing = useAICopilotStore((state) => state.isProcessing);
  const currentRoute = useAICopilotStore((state) => state.currentRoute);
  const activeEntity = useAICopilotStore((state) => state.activeEntity);
  const screenSnapshot = useAICopilotStore((state) => state.screenSnapshot);
  const snapshotMeta = useAICopilotStore((state) => state.snapshotMeta);
  const activeAnomalies = useAICopilotStore((state) => state.activeAnomalies);
  const messages = useAICopilotStore((state) => state.messages);
  const toggleCopilot = useAICopilotStore((state) => state.toggleCopilot);
  const setProcessing = useAICopilotStore((state) => state.setProcessing);
  const addMessage = useAICopilotStore((state) => state.addMessage);
  const clearConversation = useAICopilotStore((state) => state.clearConversation);

  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isOpen, messages, activeAnomalies]);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  const topAnomalies = useMemo(() => activeAnomalies.slice(0, 5), [activeAnomalies]);

  async function handleSend(prefilled?: string) {
    const raw = (prefilled ?? input).trim();
    if (!raw || isProcessing) return;

    setError(null);
    setProcessing(true);
    if (!prefilled) setInput("");
    addMessage({ role: "user", content: raw });

    try {
      const fullPrompt = buildContextEnvelope(useAICopilotStore.getState(), raw);
      const result = await sendAiMessage(fullPrompt, sessionId);
      setSessionId(result.sessionId);
      addMessage({ role: "assistant", content: result.reply });
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : "تعذر الوصول إلى المساعد الآن. حاول مرة أخرى.";
      setError(message);
      addMessage({
        role: "assistant",
        content: `تعذر تنفيذ الطلب الآن.\n- السبب: ${message}\n- يمكنك إعادة المحاولة أو فتح الشاشة الحالية ومراجعة التنبيهات أولاً.`,
      });
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  }

  function handleResetConversation() {
    setSessionId(undefined);
    setError(null);
    clearConversation();
    setInput("");
  }

  const quickPrompts = [
    "ما أهم المخاطر الظاهرة في هذه الشاشة الآن؟",
    "اشرح لي الأخطاء الحالية وما المطلوب إصلاحه أولاً.",
    "لخص الوضع الحالي بشكل تنفيذي قصير.",
  ];

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/30"
          aria-label="إغلاق المساعد"
          onClick={() => toggleCopilot(false)}
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 right-0 z-50 flex w-[420px] max-w-[92vw] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200",
          isOpen ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
        dir="rtl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">AI Copilot</div>
            <div className="text-[11px] text-slate-500">مساعد محاسبي سياقي يعمل على الشاشة الحالية</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
              onClick={handleResetConversation}
            >
              محادثة جديدة
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
              onClick={() => toggleCopilot(false)}
            >
              إغلاق
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px]">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded border border-slate-200 bg-white px-2 py-1">
              <div className="text-slate-500">المسار</div>
              <div className="truncate font-medium text-slate-800">{currentRoute || "-"}</div>
            </div>
            <div className="rounded border border-slate-200 bg-white px-2 py-1">
              <div className="text-slate-500">الكيان النشط</div>
              <div className="truncate font-medium text-slate-800">
                {activeEntity.label || activeEntity.type || "-"}
              </div>
            </div>
            <div className="rounded border border-slate-200 bg-white px-2 py-1">
              <div className="text-slate-500">نوع المشهد</div>
              <div className="truncate font-medium text-slate-800">{screenSnapshot?.viewType || "-"}</div>
            </div>
            <div className="rounded border border-slate-200 bg-white px-2 py-1">
              <div className="text-slate-500">آخر تحديث للسياق</div>
              <div className="truncate font-medium text-slate-800">{snapshotMeta.capturedAt || "-"}</div>
            </div>
          </div>

          <div className="mt-2">
            <div className="mb-1 text-slate-500">التنبيهات النشطة</div>
            {topAnomalies.length === 0 ? (
              <div className="rounded border border-slate-200 bg-white px-2 py-2 text-slate-500">
                لا توجد anomalies نشطة حاليًا.
              </div>
            ) : (
              <div className="space-y-1">
                {topAnomalies.map((item) => (
                  <div
                    key={item.id}
                    className={[
                      "rounded border px-2 py-1.5",
                      item.severity === "critical"
                        ? "border-rose-300 bg-rose-50 text-rose-700"
                        : item.severity === "warning"
                          ? "border-amber-300 bg-amber-50 text-amber-700"
                          : "border-blue-300 bg-blue-50 text-blue-700",
                    ].join(" ")}
                  >
                    {item.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50 px-4 py-3">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700">
                المساعد يعرف الآن:
                <ul className="mt-2 list-disc list-inside space-y-1 text-slate-600">
                  <li>المسار الحالي داخل التطبيق</li>
                  <li>الكيان النشط على الشاشة</li>
                  <li>ملخص الأرقام المعروضة</li>
                  <li>الصفوف المرئية من الجريد عند الحاجة</li>
                  <li>الـ anomalies الناتجة من rules engine</li>
                </ul>
              </div>
              <div className="space-y-2">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-right text-xs text-slate-700 hover:bg-slate-50"
                    onClick={() => void handleSend(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => {
                const isUser = msg.role === "user";
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isUser ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={[
                        "max-w-[90%] rounded-xl px-3 py-2 text-xs shadow-sm",
                        isUser
                          ? "bg-slate-900 text-white"
                          : "border border-slate-200 bg-white text-slate-800",
                      ].join(" ")}
                    >
                      {isUser ? <div>{msg.content}</div> : <AiMarkdown content={msg.content} />}
                    </div>
                  </div>
                );
              })}
              {isProcessing && (
                <div className="flex justify-end">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 shadow-sm">
                    <span className="inline-flex gap-1">
                      <span className="animate-bounce">●</span>
                      <span className="animate-bounce [animation-delay:120ms]">●</span>
                      <span className="animate-bounce [animation-delay:240ms]">●</span>
                    </span>
                  </div>
                </div>
              )}
              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {error}
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white p-3">
          <div className="mb-2 text-[11px] text-slate-500">
            يتم إرسال ملخص الشاشة الحالية والتنبيهات النشطة تلقائيًا مع سؤالك.
          </div>
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              rows={3}
              className="min-h-[72px] flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="اسأل عن المخاطر، التفسير، أو الإجراء التالي المناسب..."
            />
            <button
              type="button"
              disabled={isProcessing || !input.trim()}
              onClick={() => void handleSend()}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              إرسال
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

